import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Skeleton } from '@mui/material'
import clsx from 'clsx'
import { EmptySection, HeadSection } from 'src/components/elements'
import { HeartFillIcon } from '@svgs'
import { useGetPropertySearchesQuery } from 'src/redux/api'
import { PropertiesTab } from './tabs'
import ErrorState from 'src/components/elements/error-state/ErrorState'

interface IWishlistItems {}

type TabPropertiesOptions = 'all' | 'house' | 'apartment'
type TabPropertiesItem = { id: string; label: string; count?: number }

const WishlistItems: FC<IWishlistItems> = () => {
  const { t } = useTranslation()
  const {
    data: tabs,
    isLoading,
    isError,
    refetch,
  } = useGetPropertySearchesQuery({ onlyFavorites: true }, { refetchOnMountOrArgChange: true })
  const [activeTab, setActiveTab] = useState<TabPropertiesOptions>('all')
  const [isEmpty, setIsEmpty] = useState(true)

  // At-a-glance summary: how many listings the user has saved in total, so the
  // favourites page opens with a number, not just tabs (owner feedback).
  const totalSaved = (tabs || []).reduce(
    (acc: number, tab: TabPropertiesItem) => acc + (tab.count || 0),
    0,
  )

  useEffect(() => {
    if (tabs) {
      setActiveTab(tabs?.[0]?.id as TabPropertiesOptions)
      setIsEmpty(tabs?.length === 0)
    }
  }, [tabs])

  if (isError) {
    return <ErrorState onRetry={refetch} />
  }

  return (
    <section className="flex flex-col gap-4 mt-1.5">
      {isLoading ? (
        <Skeleton variant="rounded" height={423} />
      ) : isEmpty ? (
        // Wishlist-specific copy: the default emptySection texts talk about
        // creating a запит and made no sense here (39.8)
        <EmptySection
          title={t('emptyWishlist.title')}
          description={t('emptyWishlist.description')}
          buttonText={t('emptyWishlist.buttonText')}
          buttonLink="/"
          buttonKind="go"
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 min-h-7 px-3 py-1 rounded-full bg-muted-soft text-sm font-bold text-ink">
              <span className="icon-button w-3.5 h-3.5 fill-danger">
                <HeartFillIcon />
              </span>
              {t('wishlist.saved_summary', { count: totalSaved })}
            </span>
          </div>
          <HeadSection isSearch isSorting>
            {/* One saved-search group needs no tab bar — its name (often a test
                label like "фыв") is noise; the summary chip already shows the
                count. Tabs appear only when there are several groups. */}
            {tabs && tabs.length > 1 && (
              <div className="flex flex-col md:mx-0 ">
                <nav className="flex overflow-x-auto  md:m-0 md:p-0 -mx-4 px-4 pr-4">
                  {tabs?.map((tab: TabPropertiesItem) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as TabPropertiesOptions)}
                      className={clsx(
                        'shrink-0 h-11 px-4 text-center min-w-[6.4375rem] relative inline-flex gap-4 text-base items-center justify-center transition-all duration-300',
                        activeTab === tab.id
                          ? 'text-brand-ink font-extrabold'
                          : 'text-ink font-bold hover:text-brand-ink',
                      )}
                    >
                      <span>{tab.label}</span>
                      {tab.count !== undefined && (
                        <span
                          className={clsx(
                            'inline-flex items-center justify-center text-center p-1 min-w-6 h-6 text-xs font-bold text-ink rounded-full border border-solid border-ink/[0.18] transition-all duration-300',
                            activeTab === tab.id &&
                              '!border-brand-strong bg-brand-strong text-white',
                          )}
                        >
                          {tab.count}
                        </span>
                      )}
                      <span
                        className={`h-0.5 bg-brand absolute bottom-0 left-1/2 -translate-x-1/2 z-10 pointer-events-none transition-all duration-300 ${
                          activeTab === tab.id ? 'w-full' : 'w-0'
                        }`}
                      />
                    </button>
                  ))}
                </nav>
                <hr className="shrink-0 self-stretch h-px m-0 border-0 bg-ink/[0.12] w-full -mt-px" />
              </div>
            )}
          </HeadSection>

          <PropertiesTab propertySearchId={activeTab} onlyFavorites />
        </div>
      )}
    </section>
  )
}

export default WishlistItems
